import { z } from 'zod';
import { Priority, WorkType } from './enums.js';
import { IsoDateTime, LongMarkdown, Uuid } from './common.js';

/** Look for existing work or requests that describe the same thing (suggestions only, never links). */
export const FindSimilar = z
  .object({
    kind: z.enum(['WORK', 'REQUEST']),
    title: z.string().trim().min(3).max(240),
    descriptionMarkdown: LongMarkdown.optional(),
    /** The record being edited, left out of the results. */
    excludeId: Uuid.optional(),
    /** true asks the AI model to judge the candidates by meaning; false only searches text. */
    ai: z.boolean().optional(),
  })
  .strict();
export type FindSimilar = z.infer<typeof FindSimilar>;

export const SimilarRecord = z.object({
  id: Uuid,
  key: z.string(),
  title: z.string(),
  /** Work phase or request status. */
  status: z.string(),
  /** HIGH and MEDIUM come from the AI judgment; LOW is a text match it was not asked about or did not keep. */
  confidence: z.enum(['HIGH', 'MEDIUM', 'LOW']),
  reason: z.string().nullable(),
});
export type SimilarRecord = z.infer<typeof SimilarRecord>;

export const SimilarRecords = z.object({
  candidates: z.array(SimilarRecord),
  /** search: text similarity only; ai / fixture: candidates judged by a model. */
  generatedBy: z.enum(['search', 'live', 'fixture']),
});
export type SimilarRecords = z.infer<typeof SimilarRecords>;

/** AI suggestion for triaging a request: never applied by itself; a triager accepts each part. */
export const TriageSuggestion = z.object({
  priority: Priority,
  priorityReason: z.string(),
  team: z.object({ id: Uuid, name: z.string() }).nullable(),
  project: z.object({ id: Uuid, key: z.string(), name: z.string() }).nullable(),
  workType: WorkType,
  labels: z.array(z.string()),
  /** One-paragraph restatement of the problem for the work item. */
  summary: z.string(),
  /** What the triager should ask the requester, if something important is missing. */
  missingInformation: z.string().nullable(),
  generatedBy: z.enum(['live', 'fixture']),
  generatedAt: IsoDateTime,
});
export type TriageSuggestion = z.infer<typeof TriageSuggestion>;
